import { inject, Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { ResetStateDefaults } from '../base-action-class.model';
import { ChecklistFillOutAction } from './checklist-fill-out.action';
import { finalize, from, map, mergeMap, Observable, of, switchMap, throwError, timer, zip } from 'rxjs';
import { JobSerialization } from '../../enums/job/JobSerialization';
import { Job } from '../../models/job/Job';
import { CompoApp } from '../../services/CompoApp';
import {
  WorkflowOverviewItem,
  WorkflowOverviewItemState
} from '../../models/workflow-overview/workflow-overview.model';
import { ChecklistRequestService } from '../../services/request/checklist-request.service';
import { ChecklistRevision } from '../../models/checklist/checklist-revision.model';
import { ChecklistEntry } from '../../models/checklist/checklist-entry-model';
import { ChecklistEntryType } from '../../enums/checklist-entry/checklist-entry-type.enum';
import { ChecklistTemplate } from '../../models/checklist-template/checklist-template.model';
import { ChecklistTemplateEntry } from '../../models/checklist-template-entry/checklist-template-entry.model';
import { ChecklistFillOutData } from '../../models/checklist/checklist-fill-out-data.model';
import { ChecklistBlockItem } from '../../models/checklist/checklist-block-item.model';
import { JobStatus } from '../../enums/job/JobStatus';
import { ChecklistEntryRenderOption } from '../../enums/checklist-template/checklist-entry-render-option';
import { ComparisonOperator } from '../../enums/comparison-operator/comparison-operator.enum';
import {
  ChecklistTemplateEntryRange
} from '../../models/checklist-template-entry/checklist-template-entry-range.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ChecklistTemplateWorkflowOverviewBuilder } from '../../utils/checklist-template-workflow-overview-builder';
import { ChecklistStatus } from '../../enums/checklist/checklist-status.enum';
import { catchError, delay, tap } from 'rxjs/operators';
import { RequestClientService } from '../../services/client/request-client.service';
import { JobFillOutAction } from '../job-fill-out/job-fill-out.action';
import { ChecklistFillOutFilter } from '../../enums/checklist/checklist-fill-out-filter.enum';
import {
  ChecklistTemplateVersionEntryRangeType
} from '../../enums/checklist-template/checklist-template-version-entry-range-type.enum';
import Constants from '../../Constants';
import { ChecklistPreviewData } from '../../models/checklist/checklist-preview-data.model';
import { JobTemplateModel } from '../../models/job_template/job-template.model';
import { FillOutInfoMode } from '../../enums/checklist/checklist-fill-out-info-mode.enum';
import { ChatCounts } from '../../models/chat/chat-counts.model';
import { TranslationCodeView } from '../../models/translation/TranslationCodeView';
import { ChecklistAssessmentEvaluation } from '../../models/checklist/can-evaluate-checklist-assessment.model';
import ErrorCode from '../../enums/ErrorCode';
import { RJobXsState } from '../user-rights/topic-based/job/r-job-xs.state';
import { User } from '../../models/user/User';
import {
  JobOverallAssessmentTypeDtoModel
} from '../../models/designations-and-icon/job-overall-assessment-type-dto.model';
import { ChatService } from '../../services/ChatService';
import { JobRoutineService } from '../../services/job-routine.service';
import { JobFillOutAccordion } from '../job-fill-out/job-fill-out-accordion.enum';
import { ChecklistFillOutMode, JobAccordions } from './checklist-fill-out.enum';

interface ChecklistFillOutXsStateModel {
  mode: ChecklistFillOutMode;
  fillOutInfoMode: FillOutInfoMode;
  checklistFillOutData: ChecklistFillOutData;
  checklistPreviewData: ChecklistPreviewData;
  jobId: string;
  checklistId: string;
  checklistRevisionId: string;
  checklistTemplateId: string;
  checklistTemplateVersionId: string;
  job: Job;
  jobRoutine: JobTemplateModel;
  jobRoutineTemplate: JobTemplateModel;
  checklistRevision: ChecklistRevision;
  checklistTemplate: ChecklistTemplate;
  checklistTemplateEntryIdsInOrder: string[];
  checklistTemplateEntryPageBlocks: ChecklistBlockItem[][][]; // [page][block][item]
  visibleChecklistTemplateEntryPageBlocks: ChecklistBlockItem[][][]; // [page][block][item] adapted by conditions
  checklistTemplateEntries: Map<string, ChecklistTemplateEntry>; // K: checklist template entry id, V: checklist template entry;
  checklistEntries: Map<string, ChecklistEntry>; // K: checklist entry id, V: checklist entry;
  checklistEntryIdOfChecklistTemplateEntryId: Map<string, string>; // K: checklist template entry id, V: checklist entry id;
  checklistEntryValues: Map<string, Partial<ChecklistEntry>>; // K: checklist entry id, V: checklist entry value;
  checklistEntryValuesSnapshotForFilter: Map<string, Partial<ChecklistEntry>>; // K: checklist entry id, V: checklist entry value;
  checklistEntryWorkflowState: Map<string, WorkflowOverviewItemState>; // K: checklist template entry id, V: state;
  workflowOverviewItems: WorkflowOverviewItem[];
  signEnabled: boolean;
  pages: number;
  currentPage: number;
  activeFilter: ChecklistFillOutFilter;
  notFilledAndNegativeFilter: boolean;
  showDescriptions: boolean;
  showImages: boolean;
  mostRecentRevisionId: string;
  active: boolean;
  inProgress: boolean;
  hasChanges: boolean;
  targetChecklistEntryForChat: ChecklistEntry;
  chatCounts: ChatCounts;
  assessmentEvaluation: ChecklistAssessmentEvaluation;
  visibleJobAccordions: Set<JobAccordions>;
  openJobAccordions: Set<JobAccordions>;
  ownUserRegisteredForFillOut: boolean;
}

const getDefaults: () => ChecklistFillOutXsStateModel = (): ChecklistFillOutXsStateModel => ({
  mode: null,
  fillOutInfoMode: null,
  checklistFillOutData: null,
  checklistPreviewData: null,
  jobId: null,
  checklistId: null,
  checklistRevisionId: null,
  checklistTemplateId: null,
  checklistTemplateVersionId: null,
  job: null,
  jobRoutine: null,
  jobRoutineTemplate: null,
  checklistRevision: null,
  checklistTemplate: null,
  checklistTemplateEntryIdsInOrder: null,
  checklistTemplateEntryPageBlocks: null,
  visibleChecklistTemplateEntryPageBlocks: null,
  checklistTemplateEntries: null,
  checklistEntries: null,
  checklistEntryIdOfChecklistTemplateEntryId: new Map<string, string>(),
  checklistEntryValues: new Map<string, Partial<ChecklistEntry>>(),
  checklistEntryValuesSnapshotForFilter: new Map<string, Partial<ChecklistEntry>>(),
  checklistEntryWorkflowState: new Map<string, WorkflowOverviewItemState>(),
  workflowOverviewItems: null,
  signEnabled: null,
  pages: null,
  currentPage: null,
  activeFilter: null,
  notFilledAndNegativeFilter: false,
  showDescriptions: true,
  showImages: true,
  mostRecentRevisionId: null,
  active: false,
  inProgress: false,
  hasChanges: false,
  targetChecklistEntryForChat: null,
  chatCounts: null,
  assessmentEvaluation: null,
  visibleJobAccordions: new Set<JobAccordions>(),
  openJobAccordions: new Set<JobAccordions>(),
  ownUserRegisteredForFillOut: false
});

@State<ChecklistFillOutXsStateModel>({
  name: 'checklistFillOut',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistFillOutXsState {

  //region READONLY FIELDS
  private static REQUEST_DELAY: number = 500;
  private NEGATIVE_OVERALL_ASSESSMENT_IDS = JobOverallAssessmentTypeDtoModel.NEGATIVE_OVERALL_ASSESSMENT_IDS;
  readonly #chatService = inject(ChatService);
  //endregion

  //region INJECTIONS
  readonly #jobRoutineTemplate = inject(JobRoutineService);
  private app = inject(CompoApp);
  private store = inject(Store);
  private checklistRequestService = inject(ChecklistRequestService);
  private requestService = inject(RequestClientService);
  //endregion

  //region SELECTORS
  @Selector()
  static mode(state: ChecklistFillOutXsStateModel): ChecklistFillOutMode {
    return state.mode;
  }

  @Selector()
  static fillOutInfoMode(state: ChecklistFillOutXsStateModel): FillOutInfoMode {
    return state.fillOutInfoMode;
  }

  @Selector([
    ChecklistFillOutXsState.mode,
    ChecklistFillOutXsState.signed,
    ChecklistFillOutXsState.ownUserRegisteredForFillOut,
    RJobXsState.canEditJob
  ])
  static fillOutEnabled(
    mode: ChecklistFillOutMode,
    signed: boolean,
    ownUserRegisteredForFillOut: boolean,
    canEditJob: boolean
  ): boolean {
    switch (mode) {
      case ChecklistFillOutMode.PREVIEW: {
        return true;
      }
      case ChecklistFillOutMode.FILL_OUT:
      default: {
        return !signed && ownUserRegisteredForFillOut && canEditJob;
      }
    }
  }

  @Selector()
  static jobId(state: ChecklistFillOutXsStateModel): string {
    return state.jobId;
  }

  @Selector()
  static checklistId(state: ChecklistFillOutXsStateModel): string {
    return state.checklistId;
  }

  @Selector()
  static checklistRevisionId(state: ChecklistFillOutXsStateModel): string {
    return state.checklistRevisionId;
  }

  @Selector()
  static job(state: ChecklistFillOutXsStateModel): Job {
    return state.job;
  }

  @Selector()
  static jobRoutine(state: ChecklistFillOutXsStateModel): JobTemplateModel {
    return state.jobRoutine;
  }

  @Selector()
  static jobRoutineTemplate(state: ChecklistFillOutXsStateModel): JobTemplateModel {
    return state.jobRoutineTemplate;
  }

  @Selector()
  static revision(state: ChecklistFillOutXsStateModel): ChecklistRevision | null {
    return state.checklistRevision;
  }

  @Selector()
  static checklistTemplateVersionNumber(state: ChecklistFillOutXsStateModel): string {
    return state.checklistTemplate?.currentVersion?.version + '.0' || 'x.x';
  }

  @Selector()
  static checklistName(state: ChecklistFillOutXsStateModel): TranslationCodeView | undefined {
    return state.checklistTemplate?.name;
  }

  @Selector()
  static checklistEntryForChat(state: ChecklistFillOutXsStateModel): ChecklistEntry | null {
    return state.targetChecklistEntryForChat;
  }

  @Selector()
  static chatCounts(state: ChecklistFillOutXsStateModel): ChatCounts {
    return state.chatCounts;
  }

  @Selector()
  static assessmentEvaluation(state: ChecklistFillOutXsStateModel): ChecklistAssessmentEvaluation {
    return state.assessmentEvaluation;
  }

  @Selector()
  static assessmentIsRequired(state: ChecklistFillOutXsStateModel): boolean {
    return state.checklistTemplate?.currentVersion?.assessmentIsRequired ?? false;
  }

  @Selector()
  static visibleChecklistBlockItemsForPage(state: ChecklistFillOutXsStateModel): ChecklistBlockItem[][] {
    const page: number = state.currentPage;

    if (!state.visibleChecklistTemplateEntryPageBlocks) {
      return [];
    }

    return state.visibleChecklistTemplateEntryPageBlocks[page - 1];
  }

  @Selector()
  static showNavigation(state: ChecklistFillOutXsStateModel): boolean {
    return state.active && state.pages > 1;
  }

  @Selector()
  static moveBackwardEnabled(state: ChecklistFillOutXsStateModel): boolean {
    return state.active && !state.inProgress && state.currentPage > 1;
  }

  @Selector()
  static moveForwardEnabled(state: ChecklistFillOutXsStateModel): boolean {
    return state.active && !state.inProgress && state.currentPage < state.pages;
  }

  @Selector()
  static workflowOverviewItems(state: ChecklistFillOutXsStateModel): WorkflowOverviewItem[] | null {
    return state.workflowOverviewItems;
  }

  @Selector()
  static visibleJobAccordions(state: ChecklistFillOutXsStateModel): Set<JobAccordions> | null {
    return state.visibleJobAccordions;
  }

  @Selector()
  static openJobAccordions(state: ChecklistFillOutXsStateModel): Set<JobAccordions> | null {
    return state.openJobAccordions;
  }

  static checklistTemplateEntry(checklistTemplateEntryId: string) {
    return createSelector(
      [ChecklistFillOutXsState],
      (state: ChecklistFillOutXsStateModel): ChecklistTemplateEntry | null => {
        if (checklistTemplateEntryId === null) {
          return null;
        }
        return state.checklistTemplateEntries?.get(checklistTemplateEntryId) || null;
      });
  }

  static checklistEntry(checklistEntryId: string) {
    return createSelector(
      [ChecklistFillOutXsState],
      (state: ChecklistFillOutXsStateModel): ChecklistEntry | null => {
        if (checklistEntryId === null) {
          return null;
        }
        return state.checklistEntries?.get(checklistEntryId) || null;
      });
  }

  static checklistEntryValue(checklistEntryId: string) {
    return createSelector(
      [ChecklistFillOutXsState],
      (state: ChecklistFillOutXsStateModel): Partial<ChecklistEntry> | null => {
        if (checklistEntryId === null) {
          return null;
        }
        return state.checklistEntryValues?.get(checklistEntryId) || null;
      });
  }

  @Selector()
  static signEnabled(state: ChecklistFillOutXsStateModel): boolean {
    if (state.mode === ChecklistFillOutMode.FILL_OUT) {
      return state.signEnabled && state.checklistRevision?.status !== ChecklistStatus.FILLED_AND_SIGNED;
    } else {
      return false;
    }
  }

  @Selector()
  static signed(state: ChecklistFillOutXsStateModel): boolean {
    if (state.mode === ChecklistFillOutMode.FILL_OUT) {
      return state.checklistRevision?.status === ChecklistStatus.FILLED_AND_SIGNED;
    } else {
      return false;
    }
  }

  @Selector()
  static activeFilter(state: ChecklistFillOutXsStateModel): ChecklistFillOutFilter | null {
    return state.activeFilter;
  }

  @Selector()
  static showDescriptions(state: ChecklistFillOutXsStateModel): boolean {
    return state.showDescriptions;
  }

  @Selector()
  static showImages(state: ChecklistFillOutXsStateModel): boolean {
    return state.showImages;
  }

  @Selector()
  static notFilledAndNegativeFilter(state: ChecklistFillOutXsStateModel): boolean {
    return state.notFilledAndNegativeFilter;
  }

  @Selector()
  static hasChanges(state: ChecklistFillOutXsStateModel): boolean {
    return state.hasChanges;
  }

  @Selector()
  static isMostRecentRevision(state: ChecklistFillOutXsStateModel): boolean | null {
    return state.checklistRevision?.isMostRecentRevision;
  }

  @Selector()
  static mostRecentRevisionId(state: ChecklistFillOutXsStateModel): string | null {
    return state.mostRecentRevisionId;
  }

  @Selector()
  static active(state: ChecklistFillOutXsStateModel): boolean {
    return state.active;
  }

  @Selector()
  static inProgress(state: ChecklistFillOutXsStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static saveRequired(state: ChecklistFillOutXsStateModel): boolean {
    return true;
  }

  @Selector()
  static ownUserRegisteredForFillOut(state: ChecklistFillOutXsStateModel): boolean {
    return state.ownUserRegisteredForFillOut;
  }

  @Selector()
  static activeFillingOutUser(state: ChecklistFillOutXsStateModel): User {
    return state.checklistRevision?.activeFillingOutUser;
  }

  //endregion

  //region ACTIONS
  @Action(ChecklistFillOutAction.InitFillOut)
  public initFillOut(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.InitFillOut
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    if (action.jobId == null) {
      return throwError(() => new Error('Job id cannot be null!'));
    }

    if (action.checklistId === null) {
      return throwError(() => new Error('Checklist id cannot be null!'));
    }

    if (action.checklistRevisionId === null) {
      return throwError(() => new Error('Checklist revision id cannot be null!'));
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });

    return zip(
      this.checklistRequestService.getChecklistForFillOut(
        action.jobId,
        action.checklistId,
        action.checklistRevisionId
      ),
      this.app.jobService.fetchOneJobWithId(action.jobId, JobSerialization.DETAILED),
      this.checklistRequestService.registerActiveFillingOutUser(action.checklistId, action.checklistRevisionId),
      timer(ChecklistFillOutXsState.REQUEST_DELAY)
    ).pipe(
      mergeMap(([checklistFillOutData, job, registered, _]: [ChecklistFillOutData, Job, boolean, any]) => {
        return zip(
          !!job?.jobRoutineId
            ? from(this.#jobRoutineTemplate.fetchJobRoutineById(job.jobRoutineId))
            : of(null),
          job.chatId
            ? this.#chatService.getChatCounts(job.chatId)
            : of(null)
        )
          .pipe(
            map(([jobRoutine, chatCounts]: [JobTemplateModel | null, ChatCounts]) => ([
              checklistFillOutData,
              job,
              jobRoutine,
              chatCounts,
              registered
            ]))
          );
      }),
      map(([checklistFillOutData, job, jobRoutine, chatCounts, ownUserRegisteredForFillOut]
           : [ChecklistFillOutData, Job, JobTemplateModel, ChatCounts, boolean, any]
        ) => {
          const checklistEntryValues: Map<string, Partial<ChecklistEntry>> = new Map();

          if (checklistFillOutData?.checklistRevision?.entries) {
            for (const checklistEntry of checklistFillOutData.checklistRevision.entries) {
              if (checklistEntry.id) {
                checklistEntryValues.set(checklistEntry.id, { ...checklistEntry });
              }
            }
          }

          const visibleJobAccordions: Set<JobAccordions> = this.getVisibleJobAccordions(job);

          ctx.patchState({
            mode: ChecklistFillOutMode.FILL_OUT,
            fillOutInfoMode: FillOutInfoMode.JOB,
            checklistFillOutData: checklistFillOutData,
            checklistPreviewData: null,
            jobId: action.jobId,
            checklistId: action.checklistId,
            checklistRevisionId: action.checklistRevisionId,
            checklistTemplateId: checklistFillOutData.checklistTemplate.id,
            job: job,
            jobRoutine: jobRoutine,
            jobRoutineTemplate: null,
            checklistRevision: checklistFillOutData.checklistRevision,
            checklistTemplate: checklistFillOutData.checklistTemplate,
            checklistEntryValues: checklistEntryValues,
            visibleJobAccordions: visibleJobAccordions,
            mostRecentRevisionId: checklistFillOutData.mostRecentRevisionId,
            chatCounts,
            active: true,
            inProgress: false,
            ownUserRegisteredForFillOut: ownUserRegisteredForFillOut
          });

          this.setEntriesAndPages(
            ctx,
            checklistFillOutData.checklistRevision.entries,
            checklistFillOutData.checklistTemplate.checklistTemplateEntries
          );
          this.setVisibleEntries(ctx);

          // set the values of workflow overview
          for (const checklistEntry of checklistFillOutData.checklistRevision.entries) {
            this.setWorkflowEntryState(checklistEntry.id, ctx);
          }

          this.setWorkflowOverview(ctx);
          this.setSignEnabled(ctx);
        }
      ),
      finalize(() => ctx.patchState({ inProgress: false }))
    );
  }

  @Action(ChecklistFillOutAction.InitPreview)
  public initPreview(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.InitPreview
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();
    if (state.active) {
      return throwError(() => new Error('State is already active!'));
    }

    if (action.checklistTemplateId == null) {
      return throwError(() => new Error('Checklist template id cannot be null!'));
    }

    if (action.checklistTemplateVersionId == null) {
      return throwError(() => new Error('Checklist template version id cannot be null!'));
    }

    let fillOutInfoMode: FillOutInfoMode;
    if (!!action.jobId) {
      fillOutInfoMode = FillOutInfoMode.JOB;
    } else if (!!action.jobRoutineId) {
      fillOutInfoMode = FillOutInfoMode.JOB_ROUTINE;
    } else if (!!action.jobRoutineTemplateModuleVersionId) {
      fillOutInfoMode = FillOutInfoMode.JOB_ROUTINE_TEMPLATE;
    } else {
      fillOutInfoMode = FillOutInfoMode.NONE;
    }

    ctx.patchState({
      ...getDefaults(),
      inProgress: true
    });

    return zip(
      this.app.checklistTemplateService.getPreviewData(
        action.checklistTemplateId,
        action.checklistTemplateVersionId
      ),
      action.jobId
        ? from(this.app.jobService.fetchOneJobWithId(action.jobId, JobSerialization.DETAILED))
          .pipe(switchMap((job: Job) => {
            const source: Observable<JobTemplateModel | null> = !!job?.jobRoutineId
              ? from(this.#jobRoutineTemplate.fetchJobRoutineById(job.jobRoutineId))
              : of(null);
            return source.pipe(map((jobRoutine: JobTemplateModel) => [job, jobRoutine]));
          }))
        : of([null, null]),
      action.jobRoutineId
        ? this.#jobRoutineTemplate.fetchJobRoutineById(action.jobRoutineId)
        : of(null),
      action.jobRoutineTemplateModuleVersionId
        ? this.app.jobRoutineTemplateService.getByVersionId(action.jobRoutineTemplateModuleVersionId)
        : of(null),
      timer(ChecklistFillOutXsState.REQUEST_DELAY)
    ).pipe(
      map(
        ([checklistPreviewData, [job, routineOfJob], jobRoutine, jobRoutineTemplate, _]
         : [ChecklistPreviewData, [Job, JobTemplateModel], JobTemplateModel, JobTemplateModel, any]) => {
          const checklistEntryValues: Map<string, Partial<ChecklistEntry>> = new Map();

          if (checklistPreviewData?.checklistEntries) {
            for (const checklistEntry of checklistPreviewData.checklistEntries) {
              if (checklistEntry.id) {
                checklistEntryValues.set(checklistEntry.id, { ...checklistEntry });
              }
            }
          }

          const visibleJobAccordions: Set<JobAccordions> = this.getVisibleJobAccordions(job);

          ctx.patchState({
            mode: ChecklistFillOutMode.PREVIEW,
            fillOutInfoMode: fillOutInfoMode,
            checklistFillOutData: null,
            checklistPreviewData: checklistPreviewData,
            jobId: action.jobId,
            checklistId: null,
            checklistRevisionId: null,
            checklistTemplateId: action.checklistTemplateId,
            checklistTemplateVersionId: action.checklistTemplateVersionId,
            job: job,
            jobRoutine: routineOfJob || jobRoutine,
            jobRoutineTemplate: jobRoutineTemplate,
            checklistRevision: null,
            checklistTemplate: checklistPreviewData.checklistTemplate,
            checklistEntryValues: checklistEntryValues,
            visibleJobAccordions: visibleJobAccordions,
            mostRecentRevisionId: null,
            active: true,
            inProgress: false
          });

          this.setEntriesAndPages(
            ctx,
            checklistPreviewData.checklistEntries,
            checklistPreviewData.checklistTemplate.checklistTemplateEntries
          );
          this.setVisibleEntries(ctx);

          // set the values of workflow overview
          for (const checklistEntry of checklistPreviewData.checklistEntries) {
            this.setWorkflowEntryState(checklistEntry.id, ctx);
          }

          this.setWorkflowOverview(ctx);
          this.setSignEnabled(ctx);
        }
      ),
      finalize(() => ctx.patchState({ inProgress: false }))
    );
  }

  @Action(ChecklistFillOutAction.Job.ReFetchJob)
  public reFetchJob(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.ReFetchJob
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.jobId) {
      return throwError(() => new Error('Job id is not set!'));
    }

    const initialInProgressState: boolean = state.inProgress;

    ctx.patchState({
      inProgress: true
    });

    return zip(
      this.app.jobService.fetchOneJobWithId(state.jobId, JobSerialization.DETAILED),
      timer(ChecklistFillOutXsState.REQUEST_DELAY)
    ).pipe(
      map(
        ([job, _]: [Job, any]) => {
          const openAndVisibleAccordions = this.getVisibleJobAccordions(job);
          ctx.patchState({
            job,
            inProgress: initialInProgressState === true,
            visibleJobAccordions: openAndVisibleAccordions
          });
        }
      ),
      finalize(() => ctx.patchState({ inProgress: initialInProgressState === true }))
    );
  }

  @Action(ChecklistFillOutAction.CanEvaluateAssessment)
  public canEvaluateAssessment(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      return throwError(() => new Error('Signing is only enabled is fill out mode!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestDate: Date = new Date();

    return this.checklistRequestService
      .canEvaluateAssessment(
        state.checklistId,
        state.checklistRevisionId
      )
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestDate)),
        map((assessmentEvaluation: ChecklistAssessmentEvaluation) => {
          ctx.patchState({ assessmentEvaluation });
        }),
        finalize(() => {
          ctx.patchState({ inProgress: false });
        })
      );
  }

  @Action(ChecklistFillOutAction.SignChecklist)
  public sign(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.SignChecklist
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      return throwError(() => new Error('Signing is only enabled is fill out mode!'));
    }

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!action.signature) {
      return throwError(() => new Error('Signature cannot be null!'));
    }

    if (!state.assessmentEvaluation && !action.assessmentTypeId) {
      return throwError(() => new Error('Assessment type id cannot be null!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestDate: Date = new Date();

    return this.checklistRequestService
      .sign(
        state.checklistId,
        state.checklistRevisionId,
        action.signature,
        action.personalNumber,
        action.assessmentTypeId,
        action.setJobToDone,
        action.assessmentIsRequired
      )
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestDate)),
        map(() => {
          ctx.patchState({
            checklistRevision: {
              ...state.checklistRevision,
              status: ChecklistStatus.FILLED_AND_SIGNED
            },
            signEnabled: false
          });
        }),
        finalize(() => {
          ctx.patchState({ inProgress: false });
        })
      );
  }

  @Action(ChecklistFillOutAction.PrintChecklist)
  public print(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    switch (state.mode) {
      case ChecklistFillOutMode.FILL_OUT: {
        const checklistRevisionId = state.checklistRevisionId;

        ctx.patchState({ inProgress: true });

        const urlBase = Constants.URLS.checklist.print
          .replace('{checklistRevisionId}', '' + checklistRevisionId);
        const url = urlBase + Constants.URLS.query.parameter.accessToken + this.app.data.getToken();
        const pwa = window.open(url, '_blank');
        if (!pwa || pwa.closed || typeof pwa.closed == 'undefined') {
          alert('Please disable your Pop-up blocker and try again.');
        }
        ctx.patchState({ inProgress: false });

        return of(null);
      }
      case ChecklistFillOutMode.PREVIEW: {

        const checklistEntries: ChecklistEntry[] = [];

        for (const [id, checklistEntry] of state.checklistEntryValues.entries()) {

          checklistEntries.push({
            ...checklistEntry,
            id
          } as ChecklistEntry);
        }

        ctx.patchState({ inProgress: true });

        const requestDate: Date = new Date();

        return this.checklistRequestService.printPreview(
          state.checklistTemplateId,
          state.checklistTemplateVersionId,
          checklistEntries,
          state.jobId
        )
          .pipe(
            delay(this.requestService.calcDefaultTimeoutOf(requestDate)),
            map((blobSource: any) => {
              const blob: any = new Blob([blobSource], { type: 'application/pdf' });
              const blobURL: string = URL.createObjectURL(blob);
              const pwa = window.open(blobURL, '_blank');
              if (!pwa || pwa.closed || typeof pwa.closed == 'undefined') {
                alert('Please disable your Pop-up blocker and try again.');
              }
            }),
            finalize(() => ctx.patchState({ inProgress: false }))
          );
      }
    }

  }

  @Action(ChecklistFillOutAction.SaveAndPrintChecklist)
  public saveAndPrint(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    switch (state.mode) {
      case ChecklistFillOutMode.FILL_OUT: {
        return this.store.dispatch(new ChecklistFillOutAction.SaveCurrentState())
          .pipe(
            tap(() => {
              const urlBase = Constants.URLS.checklist.print
                .replace('{checklistRevisionId}', '' + state.checklistRevisionId);
              const url = urlBase + Constants.URLS.query.parameter.accessToken + this.app.data.getToken();
              const pwa = window.open(url, '_blank');
              if (!pwa || pwa.closed || typeof pwa.closed == 'undefined') {
                alert('Please disable your Pop-up blocker and try again.');
              }
            })
          );
      }
      case ChecklistFillOutMode.PREVIEW:
      default: {
        return of();
      }
    }
  }

  @Action(ChecklistFillOutAction.Job.OpenChatAccordionForChecklistEntry)
  public openChatForEntry(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.OpenChatAccordionForChecklistEntry
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Chat is only enabled is fill out mode!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    if (!action.checklistEntry) {
      throw new Error('Checklist entry mut not be null!');
    }

    const target: JobAccordions = JobAccordions.COMMENT;
    const visibleAccordions: Set<JobAccordions> = new Set<JobAccordions>(state.visibleJobAccordions);
    visibleAccordions.add(target);

    const openAccordions: Set<JobAccordions> = new Set<JobAccordions>(state.openJobAccordions);
    openAccordions.add(target);

    ctx.patchState({
      visibleJobAccordions: visibleAccordions,
      openJobAccordions: openAccordions,
      targetChecklistEntryForChat: action.checklistEntry
    });

    setTimeout(() => {
      this.store.dispatch(new JobFillOutAction.FocusAccordion(JobFillOutAccordion.COMMENTS));
    }, 100);

  }

  @Action(ChecklistFillOutAction.Job.ClearChecklistEntryForChat)
  public clearEntryForChat(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.ClearChecklistEntryForChat
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Chat is only enabled is fill out mode!');
    }

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    ctx.patchState({
      targetChecklistEntryForChat: null
    });
  }

  @Action(ChecklistFillOutAction.Job.ReloadChatCounts)
  public reloadChatCounts(ctx: StateContext<ChecklistFillOutXsStateModel>): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    return (state.job?.chatId
        ? this.#chatService.getChatCounts(state.job.chatId)
        : of(null)
    ).pipe(map((chatCounts: ChatCounts) => {
      ctx.patchState({ chatCounts });
    }));
  }

  @Action(ChecklistFillOutAction.Job.AddVisibleJobAccordion)
  public addVisibleJobAccordion(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.AddVisibleJobAccordion
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Job accordions are only enabled in fill out mode!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const target: JobAccordions = action.accordion;
    const accordions: Set<JobAccordions> = new Set<JobAccordions>(state.visibleJobAccordions);
    accordions.add(target);

    ctx.patchState({
      visibleJobAccordions: accordions
    });
  }

  @Action(ChecklistFillOutAction.Job.RemoveVisibleJobAccordion)
  public removeVisibleJobAccordion(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.RemoveVisibleJobAccordion
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      return;
      // throw new Error('State is not active!');
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Job accordions are only enabled in fill out mode!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const target: JobAccordions = action.accordion;
    const accordions: Set<JobAccordions> = new Set<JobAccordions>(state.visibleJobAccordions);
    accordions.delete(target);

    ctx.patchState({
      visibleJobAccordions: accordions
    });
  }

  @Action(ChecklistFillOutAction.Job.OpenJobAccordion)
  public openJobAccordion(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.OpenJobAccordion
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Job accordions are only enabled in fill out mode!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const target: JobAccordions = action.accordion;
    const accordions: Set<JobAccordions> = new Set<JobAccordions>(state.openJobAccordions);
    accordions.add(target);

    ctx.patchState({
      openJobAccordions: accordions
    });
  }

  @Action(ChecklistFillOutAction.Job.CloseJobAccordion)
  public closeJobAccordion(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.Job.CloseJobAccordion
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      throw new Error('Job accordions are only enabled in fill out mode!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    const target: JobAccordions = action.accordion;
    const accordions: Set<JobAccordions> = new Set<JobAccordions>(state.openJobAccordions);
    accordions.delete(target);

    ctx.patchState({
      openJobAccordions: accordions
    });
  }

  @Action(ChecklistFillOutAction.SetEntryValue)
  public setEntryValue(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.SetEntryValue
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.inProgress) {
      throw new Error('Request is in progress!');
    }

    if (state.mode === ChecklistFillOutMode.FILL_OUT) {
      if (!state.checklistRevision) {
        throw new Error('Checklist is null!');
      }

      if (state.checklistRevision.status === ChecklistStatus.FILLED_AND_SIGNED) {
        throw new Error('Checklist is signed!');
      }
    }

    if (!action.checklistEntryId) {
      throw new Error('Checklist entry id cannot be null!');
    }

    ctx.patchState({
      hasChanges: true
    });

    if (!action.values) {
      if (state.checklistEntryValues.has(action.checklistEntryId)) {
        const checklistEntryValues = new Map<string, Partial<ChecklistEntry>>(state.checklistEntryValues);
        checklistEntryValues.delete(action.checklistEntryId);
        ctx.patchState({ checklistEntryValues });
      }
    } else {
      const checklistEntryValues = new Map<string, Partial<ChecklistEntry>>(state.checklistEntryValues);

      const existingChecklistEntryValue: Partial<ChecklistEntry> =
        checklistEntryValues.get(action.checklistEntryId) || state.checklistEntries.get(action.checklistEntryId);

      checklistEntryValues.set(action.checklistEntryId, { ...existingChecklistEntryValue, ...action.values });

      ctx.patchState({ checklistEntryValues });
    }

    const checklistEntry: ChecklistEntry = state.checklistEntries.get(action.checklistEntryId);
    const checklistTemplateEntry: ChecklistTemplateEntry =
      state.checklistTemplateEntries.get(checklistEntry.checklistTemplateEntryId);
    const checklistEntryCompareType: ChecklistEntryType = checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW
      ? checklistTemplateEntry.tableType
      : checklistTemplateEntry.type;

    if (checklistEntryCompareType === ChecklistEntryType.BOOLEAN
      || checklistEntryCompareType === ChecklistEntryType.OK_NOT_OK
      || checklistEntryCompareType === ChecklistEntryType.COUNTER
      || checklistEntryCompareType === ChecklistEntryType.LIST
      || checklistEntryCompareType === ChecklistEntryType.ASSESSMENT_LIST
      || checklistEntryCompareType === ChecklistEntryType.NUMBER
      || checklistEntryCompareType === ChecklistEntryType.DATE) {
      this.setVisibleEntries(ctx);
      this.moveCurrentPageToEntry(ctx, action.checklistEntryId);
    }

    this.setWorkflowEntryState(action.checklistEntryId, ctx);
    this.setWorkflowOverview(ctx);
    this.setSignEnabled(ctx);
  }

  @Action(ChecklistFillOutAction.MoveBackward)
  public moveBackward(ctx: StateContext<ChecklistFillOutXsStateModel>): void {
    if (!this.store.selectSnapshot(ChecklistFillOutXsState.moveBackwardEnabled)) {
      throw new Error('Move backward is not enabled!');
    }

    ctx.patchState({ currentPage: ctx.getState().currentPage - 1 });
    this.setWorkflowOverview(ctx);
  }

  @Action(ChecklistFillOutAction.MoveForward)
  public moveForward(ctx: StateContext<ChecklistFillOutXsStateModel>): void {
    if (!this.store.selectSnapshot(ChecklistFillOutXsState.moveForwardEnabled)) {
      throw new Error('Move forward is not enabled!');
    }

    ctx.patchState({ currentPage: ctx.getState().currentPage + 1 });
    this.setWorkflowOverview(ctx);
  }

  @Action([
    ChecklistFillOutAction.SaveCurrentState,
    ChecklistFillOutAction.AutoSave
  ])
  public saveCurrentState(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.SaveCurrentState | ChecklistFillOutAction.AutoSave
  ): Observable<void> {
    const state: ChecklistFillOutXsStateModel = ctx.getState();
    const autoSave: boolean = action instanceof ChecklistFillOutAction.AutoSave;

    if (!state.active) {
      return throwError(() => new Error('State is not active!'));
    }

    if (state.mode !== ChecklistFillOutMode.FILL_OUT) {
      return throwError(() => new Error('Save is only enabled is fill out mode!'));
    }

    if (!autoSave && state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!state.checklistRevision) {
      throw new Error('Checklist is null!');
    }

    if (state.checklistRevision.status === ChecklistStatus.FILLED_AND_SIGNED) {
      if (autoSave) {
        return of();
      } else {
        throw new Error('Checklist is signed!');
      }
    }

    const checklistEntries: ChecklistEntry[] = [];

    for (const [id, checklistEntry] of state.checklistEntryValues.entries()) {
      checklistEntries.push({
        ...checklistEntry,
        id
      } as ChecklistEntry);
    }

    if (!autoSave) {
      ctx.patchState({ inProgress: true });
    }

    const requestDate: Date = new Date();

    return this.checklistRequestService.saveCurrentState(
      state.checklistId,
      state.checklistRevisionId,
      checklistEntries
    )
      .pipe(
        switchMap(() => {
          if (autoSave) {
            return of(null);
          } else {
            return this.store.dispatch(new ChecklistFillOutAction.Job.ReFetchJob());
          }
        }),
        delay(autoSave ? 0 : this.requestService.calcDefaultTimeoutOf(requestDate)),
        map(() => {
          if (autoSave) {
            if (!!state.job && state.job.status < JobStatus.IN_PROGRESS) {
              ctx.patchState({
                job: {
                  ...state.job,
                  status: JobStatus.IN_PROGRESS
                }
              });
            }
            this.store.dispatch(new ChecklistFillOutAction.Job.JobStartedDueToAutosave(state.jobId));
          } else {
            this.setSignEnabled(ctx);
          }
        }),
        catchError((error: any) => {
          if (error?.error?.errorCode === ErrorCode.CHECKLIST_REVISION_NOT_MODIFIABLE) {
            ctx.patchState({
              checklistRevision: {
                ...state.checklistRevision,
                status: ChecklistStatus.FILLED_AND_SIGNED
              },
              signEnabled: false
            });
          }
          return throwError(() => error);
        }),
        finalize(() => ctx.patchState({
          inProgress: false,
          hasChanges: false
        }))
      );
  }

  @Action(ChecklistFillOutAction.ApplyFilter)
  public applyFilter(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    action: ChecklistFillOutAction.ApplyFilter
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!action.filter) {
      throw new Error('Filter cannot be null!');
    }

    if (!state.active) {
      throw new Error('State is not active!');
    }

    if (state.activeFilter) {
      throw new Error('Filter is already active!');
    }

    const checklistEntryValuesSnapshot = new Map<string, Partial<ChecklistEntry>>();
    for (const [id, value] of state.checklistEntryValues.entries()) {
      checklistEntryValuesSnapshot.set(
        id,
        { ...value }
      );
    }

    ctx.patchState({
      activeFilter: action.filter,
      notFilledAndNegativeFilter: action.filter === ChecklistFillOutFilter.ONLY_NOT_FILLED_AND_NEGATIVE,
      checklistEntryValuesSnapshotForFilter: checklistEntryValuesSnapshot
    });

    let checklistEntries: ChecklistEntry[];
    let checklistTemplateEntries: ChecklistTemplateEntry[];

    switch (state.mode) {
      case ChecklistFillOutMode.FILL_OUT: {
        checklistEntries = state.checklistFillOutData?.checklistRevision?.entries;
        checklistTemplateEntries = state.checklistFillOutData?.checklistTemplate?.checklistTemplateEntries;
        break;
      }
      case ChecklistFillOutMode.PREVIEW: {
        checklistEntries = state.checklistPreviewData?.checklistEntries;
        checklistTemplateEntries = state.checklistPreviewData?.checklistTemplate.checklistTemplateEntries;
        break;
      }
    }

    this.setEntriesAndPages(ctx, checklistEntries, checklistTemplateEntries);
    this.setVisibleEntries(ctx);
  }

  @Action(ChecklistFillOutAction.ClearFilter)
  public clearFilter(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    ctx.patchState({
      activeFilter: null,
      checklistEntryValuesSnapshotForFilter: null
    });

    let checklistEntries: ChecklistEntry[];
    let checklistTemplateEntries: ChecklistTemplateEntry[];

    switch (state.mode) {
      case ChecklistFillOutMode.FILL_OUT: {
        checklistEntries = state.checklistFillOutData?.checklistRevision?.entries;
        checklistTemplateEntries = state.checklistFillOutData?.checklistTemplate?.checklistTemplateEntries;
        break;
      }
      case ChecklistFillOutMode.PREVIEW: {
        checklistEntries = state.checklistPreviewData?.checklistEntries;
        checklistTemplateEntries = state.checklistPreviewData?.checklistTemplate.checklistTemplateEntries;
        break;
      }
    }

    this.setEntriesAndPages(ctx, checklistEntries, checklistTemplateEntries);
    this.setVisibleEntries(ctx);
  }

  @Action(ChecklistFillOutAction.ToggleNotFilledAndNegativeFilter)
  public toggleNotFilledAndNegative(
    ctx: StateContext<ChecklistFillOutXsStateModel>): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    const checklistEntryValuesSnapshot = new Map<string, Partial<ChecklistEntry>>();
    for (const [id, value] of state.checklistEntryValues.entries()) {
      checklistEntryValuesSnapshot.set(
        id,
        { ...value }
      );
    }

    ctx.patchState({
      activeFilter: !state.notFilledAndNegativeFilter ? ChecklistFillOutFilter.ONLY_NOT_FILLED_AND_NEGATIVE : null,
      notFilledAndNegativeFilter: !state.notFilledAndNegativeFilter,
      checklistEntryValuesSnapshotForFilter: checklistEntryValuesSnapshot
    });

    let checklistEntries: ChecklistEntry[];
    let checklistTemplateEntries: ChecklistTemplateEntry[];

    switch (state.mode) {
      case ChecklistFillOutMode.FILL_OUT: {
        checklistEntries = state.checklistFillOutData?.checklistRevision?.entries;
        checklistTemplateEntries = state.checklistFillOutData?.checklistTemplate?.checklistTemplateEntries;
        break;
      }
      case ChecklistFillOutMode.PREVIEW: {
        checklistEntries = state.checklistPreviewData?.checklistEntries;
        checklistTemplateEntries = state.checklistPreviewData?.checklistTemplate.checklistTemplateEntries;
        break;
      }
    }

    this.setEntriesAndPages(ctx, checklistEntries, checklistTemplateEntries);
    this.setVisibleEntries(ctx);
  }

  @Action(ChecklistFillOutAction.ToggleDescriptionsVisibility)
  public toggleDescriptionsVisibility(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    ctx.patchState({
      showDescriptions: !state.showDescriptions
    });
  }

  @Action(ChecklistFillOutAction.ToggleImagesVisibility)
  public toggleImagesVisibility(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active) {
      throw new Error('State is not active!');
    }

    ctx.patchState({
      showImages: !state.showImages
    });
  }

  @Action([
    ChecklistFillOutAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ChecklistFillOutXsStateModel>): void {
    if (ctx.getState().ownUserRegisteredForFillOut
      && ctx.getState().checklistId != null
      && ctx.getState().checklistRevisionId != null
      && ctx.getState().checklistRevision?.status !== ChecklistStatus.FILLED_AND_SIGNED
    ) {
      this.checklistRequestService
        .unregisterActiveFillingOutUser(ctx.getState().checklistId, ctx.getState().checklistRevisionId)
        .subscribe();
    }
    ctx.setState(getDefaults());
  }

  //endregion

  //region METHODS
  private getVisibleJobAccordions(job: Job): Set<JobAccordions> {
    const accordions: Set<JobAccordions> = new Set<JobAccordions>();

    if (this.filesShouldBeInitiallyVisible(job)) {
      accordions.add(JobAccordions.ADD_FILE);
    }

    if (this.spentTimeShouldBeInitiallyVisible(job)) {
      accordions.add(JobAccordions.ADD_TIME);
    }

    if (this.jobAssessmentShouldBeInitiallyVisible(job)) {
      accordions.add(JobAccordions.COMMENT);
    }

    return accordions;
  }

  private filesShouldBeInitiallyVisible(job: Job) {
    return job && job.documents.length > 0;
  }

  private spentTimeShouldBeInitiallyVisible(job: Job) {
    return job
      && job.status === JobStatus.IN_PROGRESS &&
      job.actualWorkingTime !== null &&
      job.actualWorkingTime !== 0;
  }

  private jobAssessmentShouldBeInitiallyVisible(job: Job): boolean {
    return job && !!job.overallAssessmentId;
  }

  private setEntriesAndPages(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    checklistEntries: ChecklistEntry[],
    checklistTemplateEntries: ChecklistTemplateEntry[]
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!checklistEntries || !checklistTemplateEntries) {
      return;
    }

    if (checklistTemplateEntries.length == 0) {
      ctx.patchState({
        checklistTemplateEntryPageBlocks: [],
        checklistTemplateEntries: new Map(),
        checklistEntries: new Map()
      });
      return;
    }

    // Key: checklist template entry id, Value: checklist entry id
    const checklistEntryIdOfChecklistTemplateEntryId = new Map<string, string>();
    const checklistEntryMap = new Map<string, ChecklistEntry>();

    for (const checklistEntry of checklistEntries) {
      checklistEntryMap.set(checklistEntry.id, checklistEntry);
      checklistEntryIdOfChecklistTemplateEntryId.set(checklistEntry.checklistTemplateEntryId, checklistEntry.id);
    }

    const checklistTemplateEntryIdsInOrder: string[] = [];
    const checklistTemplateEntryPageBlocks: ChecklistBlockItem[][][] = [];
    const checklistTemplateEntryMap = new Map<string, ChecklistTemplateEntry>();

    // Set block and entries
    let firstOnPage: boolean = true;
    let currentPage: ChecklistBlockItem[][] = [];
    let currentBlock: ChecklistBlockItem[] = [];

    for (const checklistTemplateEntry of checklistTemplateEntries) {
      checklistTemplateEntryIdsInOrder.push(checklistTemplateEntry.id);

      if (checklistTemplateEntry.type === ChecklistEntryType.PAGE_BREAK && !firstOnPage) {

        if (!checklistTemplateEntryMap.has(checklistTemplateEntry.id)) {
          checklistTemplateEntryMap.set(checklistTemplateEntry.id, checklistTemplateEntry);
        }

        // Pagination is disabled when filter is active
        if (!state.activeFilter) {
          currentPage.push(currentBlock);
          checklistTemplateEntryPageBlocks.push(currentPage);
          currentBlock = [];
          currentPage = [];
          firstOnPage = true;
        }

      } else {
        if (!checklistTemplateEntryMap.has(checklistTemplateEntry.id)) {
          checklistTemplateEntryMap.set(checklistTemplateEntry.id, checklistTemplateEntry);

          if (!checklistTemplateEntry.groupedWithPrevious && !firstOnPage) {
            currentPage.push(currentBlock);
            currentBlock = [];
          }

          currentBlock.push({
            checklistTemplateEntryId: checklistTemplateEntry.id,
            checklistEntryId: checklistEntryIdOfChecklistTemplateEntryId.get(checklistTemplateEntry.id),
            type: checklistTemplateEntry.type
          });
        }

        if (firstOnPage) {
          firstOnPage = false;
        }
      }
    }

    currentPage.push(currentBlock);
    checklistTemplateEntryPageBlocks.push(currentPage);

    ctx.patchState({
      checklistTemplateEntryIdsInOrder,
      checklistTemplateEntryPageBlocks,
      checklistTemplateEntries: checklistTemplateEntryMap,
      checklistEntries: checklistEntryMap,
      checklistEntryIdOfChecklistTemplateEntryId,
      currentPage: 1
    });
  }

  private setVisibleEntries(ctx: StateContext<ChecklistFillOutXsStateModel>): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active || !state.checklistTemplateEntries) {
      return;
    }

    // Set visible block and entries
    const visibleChecklistTemplateEntryPageBlocks: ChecklistBlockItem[][][] = [];
    const visibleChecklistTemplateEntryIds = new Set<string>();

    for (const [pageIndex, page] of state.checklistTemplateEntryPageBlocks.entries()) {
      const visiblePage: ChecklistBlockItem[][] = [];

      for (const block of page) {
        let visibleBlock: ChecklistBlockItem[] = [];

        for (const entry of block) {

          const checklistTemplateEntry: ChecklistTemplateEntry
            = state.checklistTemplateEntries.get(entry.checklistTemplateEntryId);

          // No filter applied
          if (!state.activeFilter) {
            if (this.hasConditions(checklistTemplateEntry)) {
              // Check conditions (OR)
              this.checkConditions(
                checklistTemplateEntry,
                state,
                visibleChecklistTemplateEntryIds,
                entry.checklistTemplateEntryId
              );
            } else if (checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW) {
              if (visibleChecklistTemplateEntryIds.has(checklistTemplateEntry.parentChecklistTemplateEntry.id)) {
                visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
              }
            } else {
              visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
            }
          }

          // Filter applied
          else {

            if (
              // These entries are not visible when filter is active
              checklistTemplateEntry.type !== ChecklistEntryType.TITLE
              && checklistTemplateEntry.type !== ChecklistEntryType.IMAGE
              && checklistTemplateEntry.type !== ChecklistEntryType.PAGE_BREAK
              // The table is added manually
              && checklistTemplateEntry.type !== ChecklistEntryType.TABLE
            ) {
              switch (state.activeFilter) {
                case ChecklistFillOutFilter.ONLY_NOT_FILLED:
                case ChecklistFillOutFilter.ONLY_NOT_FILLED_AND_NEGATIVE: {
                  const withNegativeFilter = state.activeFilter === ChecklistFillOutFilter.ONLY_NOT_FILLED_AND_NEGATIVE;

                  const compareType: ChecklistEntryType
                    = checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW
                    ? checklistTemplateEntry.tableType
                    : checklistTemplateEntry.type;

                  if (!this.entryHasValue(entry.checklistEntryId, ctx, true)) {
                    if (this.hasConditions(checklistTemplateEntry)) {
                      this.checkConditions(
                        checklistTemplateEntry,
                        state,
                        visibleChecklistTemplateEntryIds,
                        entry.checklistTemplateEntryId
                      );
                    } else if (checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW) {
                      visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                    } else {
                      visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                    }
                  } else if (withNegativeFilter) {

                    if (this.hasConditions(checklistTemplateEntry)) {
                      const visibleEntryIds = new Set<string>();
                      this.checkConditions(
                        checklistTemplateEntry,
                        state,
                        visibleEntryIds,
                        entry.checklistTemplateEntryId
                      );
                      if (!visibleEntryIds.has(checklistTemplateEntry.id)) {
                        break;
                      }
                    }

                    const entryValue: Partial<ChecklistEntry>
                      = state.checklistEntryValuesSnapshotForFilter.get(entry.checklistEntryId);
                    switch (compareType) {
                      case ChecklistEntryType.ASSESSMENT_LIST:
                      case ChecklistEntryType.BOOLEAN:
                      case ChecklistEntryType.OK_NOT_OK: {
                        const overallAssessmentId = checklistTemplateEntry.options
                          ?.find(option => option.id === entryValue.optionId)
                          ?.overallAssessment
                          ?.id;
                        if (this.NEGATIVE_OVERALL_ASSESSMENT_IDS.has(overallAssessmentId)) {
                          visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                        }
                        break;
                      }
                      case ChecklistEntryType.LIST: {
                        if (checklistTemplateEntry.renderOption === ChecklistEntryRenderOption.CHECKBOXES) {
                          const selectedOptionIds = new Set<string>(entryValue.optionIds || []);
                          const overallAssessmentIds: string[] = checklistTemplateEntry.options
                            ?.filter(option => selectedOptionIds.has(option.id))
                            ?.map(selectedOption => selectedOption.overallAssessment?.id);
                          if (overallAssessmentIds.some(id => this.NEGATIVE_OVERALL_ASSESSMENT_IDS.has(id))) {
                            visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                          }
                        } else {
                          const overallAssessmentId = checklistTemplateEntry.options
                            ?.find(option => option.id === entryValue.optionId)
                            ?.overallAssessment
                            ?.id;
                          if (this.NEGATIVE_OVERALL_ASSESSMENT_IDS.has(overallAssessmentId)) {
                            visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                          }
                        }
                        break;
                      }
                      case ChecklistEntryType.NUMBER:
                      case ChecklistEntryType.COUNTER:
                      case ChecklistEntryType.DATE: {
                        let rangeCompareValue: number = null;
                        if (compareType === ChecklistEntryType.DATE) {
                          rangeCompareValue = entryValue.dateTimeValue;
                        } else {
                          if (checklistTemplateEntry.constraint?.isDecimal) {
                            rangeCompareValue = entryValue.doubleValue;
                          } else {
                            rangeCompareValue = entryValue.integerValue;
                          }
                        }

                        const negativeAssessmentRangeIds: string[] = checklistTemplateEntry.ranges
                          .filter(range => range.overallAssessmentId
                            && this.NEGATIVE_OVERALL_ASSESSMENT_IDS.has(range.overallAssessmentId)
                          )
                          .map(range => range.id);

                        if (negativeAssessmentRangeIds.some(rangeId =>
                          this.matchesRange(rangeCompareValue, rangeId, checklistTemplateEntry.ranges))
                        ) {
                          visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
                        }
                        break;
                      }
                    }
                  }

                  break;
                }
              }


              // Add table header is necessary
              if (checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW
                && visibleChecklistTemplateEntryIds.has(entry.checklistTemplateEntryId)
                && !visibleChecklistTemplateEntryIds.has(checklistTemplateEntry.parentChecklistTemplateEntry.id)) {
                visibleChecklistTemplateEntryIds.add(checklistTemplateEntry.parentChecklistTemplateEntry.id);
                visibleBlock.push({
                  ...block.find(blockEntry =>
                    blockEntry.checklistTemplateEntryId === checklistTemplateEntry.parentChecklistTemplateEntry.id)
                });
              }
            }
            if (checklistTemplateEntry.type == ChecklistEntryType.TABLE && block.length == 1) {
              visibleChecklistTemplateEntryIds.add(entry.checklistTemplateEntryId);
            }
          }

          if (visibleChecklistTemplateEntryIds.has(entry.checklistTemplateEntryId)) {
            if (visibleBlock.every(b => b.checklistTemplateEntryId !== entry.checklistTemplateEntryId)) {
              visibleBlock.push({ ...entry });
            }
          }
        }

        // Remove table headers if there are no conditional rows
        for (const entry of block) {
          if (entry.type === ChecklistEntryType.TABLE && block.length > 1) {
            if (visibleBlock.every(visibleEntry => {
              const visibleChecklistTemplateEntry: ChecklistTemplateEntry
                = state.checklistTemplateEntries.get(visibleEntry.checklistTemplateEntryId);
              return visibleChecklistTemplateEntry.parentChecklistTemplateEntry?.id !== entry.checklistTemplateEntryId;
            })) {
              visibleBlock = visibleBlock.filter(visibleEntry =>
                visibleEntry.checklistTemplateEntryId !== entry.checklistTemplateEntryId
              );
              visibleChecklistTemplateEntryIds.delete(entry.checklistTemplateEntryId);
            }
          }
        }

        if (visibleBlock.length > 0) {
          visiblePage.push(visibleBlock);
        }
      }

      if (visiblePage.length > 0) {
        visibleChecklistTemplateEntryPageBlocks.push(visiblePage);
      }
    }

    ctx.patchState({
      visibleChecklistTemplateEntryPageBlocks,
      pages: visibleChecklistTemplateEntryPageBlocks.length
    });
  }

  private checkConditions(
    checklistTemplateEntry: ChecklistTemplateEntry,
    state: ChecklistFillOutXsStateModel,
    visibleChecklistTemplateEntryIds: Set<string>,
    checklistTemplateEntryId: string
  ): void {

    if (!checklistTemplateEntry) {
      return;
    }

    for (const condition of checklistTemplateEntry.conditions) {
      const sourceChecklistEntryId
        = state.checklistEntryIdOfChecklistTemplateEntryId.get(condition.sourceEntryId);
      const sourceChecklistTemplateEntry: ChecklistTemplateEntry =
        state.checklistTemplateEntries.get(condition.sourceEntryId);
      const sourceEntryValue: Partial<ChecklistEntry>
        = state.checklistEntryValues.get(sourceChecklistEntryId);

      if (sourceEntryValue) {

        // Check option (AND)
        if (condition.checklistTemplateEntryOptionIds?.length) {

          // Source entry RADIO
          if (sourceEntryValue.optionId != null) {
            if (condition.checklistTemplateEntryOptionIds
              .includes(sourceEntryValue.optionId)) {
              visibleChecklistTemplateEntryIds.add(checklistTemplateEntryId);
              break;
            }
          }

          // Source entry CHECKBOXES
          if (sourceEntryValue.optionIds?.length) {
            if (condition.checklistTemplateEntryOptionIds
              .every((optionId: string) =>
                sourceEntryValue.optionIds.includes(optionId))) {
              visibleChecklistTemplateEntryIds.add(checklistTemplateEntryId);
              break;
            }
          }
        }

        // Check range
        if (condition.checklistTemplateEntryRangeId) {
          const sourceChecklistEntryCompareType: ChecklistEntryType
            = sourceChecklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW
            ? sourceChecklistTemplateEntry.tableType
            : sourceChecklistTemplateEntry.type;

          let rangeCompareValue: number = null;

          switch (sourceChecklistEntryCompareType) {
            case ChecklistEntryType.NUMBER: {
              if (checklistTemplateEntry.constraint?.isDecimal) {
                rangeCompareValue = sourceEntryValue.doubleValue;
              } else {
                rangeCompareValue = sourceEntryValue.integerValue;
              }
              break;
            }
            case ChecklistEntryType.COUNTER: {
              if (checklistTemplateEntry.constraint?.isDecimal) {
                rangeCompareValue = sourceEntryValue.doubleValue;
              } else {
                rangeCompareValue = sourceEntryValue.integerValue;
              }
              if (!!rangeCompareValue) {
                rangeCompareValue = sourceChecklistTemplateEntry.renderOption === ChecklistEntryRenderOption.COUNTER_ASC ?
                  rangeCompareValue - Number(sourceEntryValue.initialValueFromObjectAttributeAtCreation) :
                  Number(sourceEntryValue.initialValueFromObjectAttributeAtCreation) - rangeCompareValue;
              }
              break;
            }
            case ChecklistEntryType.DATE: {
              rangeCompareValue = sourceEntryValue.dateTimeValue;
              break;
            }
          }

          if (rangeCompareValue != null) {
            let matchesRange: boolean;
            if (condition.range.rangeType === ChecklistTemplateVersionEntryRangeType.CONDITION) {
              matchesRange = this.matchesRange(
                rangeCompareValue,
                condition.checklistTemplateEntryRangeId,
                [condition.range]
              );
            } else {
              matchesRange = this.matchesRange(
                rangeCompareValue,
                condition.checklistTemplateEntryRangeId,
                sourceChecklistTemplateEntry.ranges
              );
            }
            if (matchesRange) {
              visibleChecklistTemplateEntryIds.add(checklistTemplateEntryId);
              break;
            }
          }
        }

      }
    }
  }

  private hasConditions(checklistTemplateEntry: ChecklistTemplateEntry): boolean {
    return checklistTemplateEntry?.conditions?.length > 0;
  }

  private matchesRange(value: number, rangeId: string, ranges: ChecklistTemplateEntryRange[]): boolean {
    if (!ranges) {
      return false;
    }

    // Ensure that the ranges are checked in order
    const orderedRanges: ChecklistTemplateEntryRange[] = [...ranges]
      .sort((a, b) => {
        if (a.order < b.order) {
          return -1;
        }
        if (a.order > b.order) {
          return 1;
        }
        return 0;
      });

    for (const range of orderedRanges) {
      if (range.defaultRange) {
        return range.id === rangeId;
      } else {
        let lowerLimitMatch: boolean = false;
        let upperLimitMatch: boolean = false;

        if (range.lowerLimit !== null) {
          switch (range.lowerLimitComparisonOperator) {
            case ComparisonOperator.LESS_THAN: {
              lowerLimitMatch = value > range.lowerLimit;
              break;
            }
            case ComparisonOperator.LESS_THAN_OR_EQUAL: {
              lowerLimitMatch = value >= range.lowerLimit;
              break;
            }
          }
        } else {
          lowerLimitMatch = true;
        }

        if (range.upperLimit !== null) {
          switch (range.upperLimitComparisonOperator) {
            case ComparisonOperator.LESS_THAN: {
              upperLimitMatch = value < range.upperLimit;
              break;
            }
            case ComparisonOperator.LESS_THAN_OR_EQUAL: {
              upperLimitMatch = value <= range.upperLimit;
              break;
            }
          }
        } else {
          upperLimitMatch = true;
        }

        if (lowerLimitMatch && upperLimitMatch) {
          return range.id === rangeId;
        }
      }
    }

    return false;
  }

  private moveCurrentPageToEntry(
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    checklistEntryId: string
  ): void {
    if (!checklistEntryId) {
      return;
    }

    const state: ChecklistFillOutXsStateModel = ctx.getState();

    let currentPage: number = 1;

    for (const page of state.visibleChecklistTemplateEntryPageBlocks) {
      for (const block of page) {
        for (const entry of block) {
          if (entry.checklistEntryId === checklistEntryId) {
            ctx.patchState({
              currentPage
            });
            return;
          }
        }
      }

      currentPage++;
    }
  }

  private setWorkflowOverview(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active || !state.checklistTemplateEntryIdsInOrder || !state.checklistTemplateEntries) {
      return;
    }

    const visibleChecklistTemplateEntryIds = new Set<string>();

    for (const page of state.visibleChecklistTemplateEntryPageBlocks) {
      for (const block of page) {
        for (const item of block) {
          visibleChecklistTemplateEntryIds.add(item.checklistTemplateEntryId);
        }
      }
    }

    const disabledChecklistTemplateEntries: Map<string, { label: string, icon: IconProp }> = new Map();

    for (const checklistTemplateEntryId of state.checklistTemplateEntryIdsInOrder) {
      if (!visibleChecklistTemplateEntryIds.has(checklistTemplateEntryId)) {
        disabledChecklistTemplateEntries.set(
          checklistTemplateEntryId,
          {
            label: 'DESIGN_B.CHECKLIST_FILL_OUT.CONDITIONALLY_HIDDEN_WORKFLOW_ITEM_TOOLTIP',
            icon: ['fas', 'diagram-subtask']
          }
        );
      }
    }

    const overviewItems: WorkflowOverviewItem[] = ChecklistTemplateWorkflowOverviewBuilder.buildTreeForWorkflowOverview(
      state.checklistTemplateEntryIdsInOrder,
      state.checklistTemplateEntries,
      disabledChecklistTemplateEntries,
      state.checklistEntryWorkflowState,
      false,
      true
    );

    ctx.patchState({ workflowOverviewItems: overviewItems });
  }

  private setWorkflowEntryState(
    checklistEntryId: string,
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    if (!checklistEntryId) {
      return;
    }

    const state: ChecklistFillOutXsStateModel = ctx.getState();
    const checklistEntry: ChecklistEntry = state.checklistEntries.get(checklistEntryId);
    const checklistTemplateEntry: ChecklistTemplateEntry
      = state.checklistTemplateEntries.get(checklistEntry.checklistTemplateEntryId);

    const entryHasValue: boolean = this.entryHasValue(checklistEntryId, ctx);

    const checklistEntryWorkflowState = new Map<string, WorkflowOverviewItemState>(state.checklistEntryWorkflowState);

    if (entryHasValue) {
      checklistEntryWorkflowState.set(checklistTemplateEntry.id, WorkflowOverviewItemState.FILLED_AND_CHECKED);
    } else {
      checklistEntryWorkflowState.set(checklistTemplateEntry.id, WorkflowOverviewItemState.NOT_FILLED);
    }

    if (checklistEntry.type === ChecklistEntryType.TABLE_ROW) {
      const parentChecklistTemplateEntryId = checklistTemplateEntry.parentChecklistTemplateEntry.id;

      const siblingTableRowChecklistEntryValues: boolean[] = [...state.checklistEntries.values()]
        .filter((siblingChecklistEntry: ChecklistEntry) => {
          const siblingChecklistTemplateEntry: ChecklistTemplateEntry
            = state.checklistTemplateEntries.get(siblingChecklistEntry.checklistTemplateEntryId);
          return siblingChecklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW
            && siblingChecklistTemplateEntry.parentChecklistTemplateEntry.id === parentChecklistTemplateEntryId;
        })
        .map((checklistEntry: ChecklistEntry) => checklistEntry.id)
        .map((checklistEntryId: string) => this.entryHasValue(checklistEntryId, ctx));

      if (siblingTableRowChecklistEntryValues.every((hasValue: boolean) => hasValue)) {
        checklistEntryWorkflowState.set(parentChecklistTemplateEntryId, WorkflowOverviewItemState.FILLED_AND_CHECKED);
      } else if (siblingTableRowChecklistEntryValues.some((hasValue: boolean) => hasValue)) {
        checklistEntryWorkflowState.set(parentChecklistTemplateEntryId, WorkflowOverviewItemState.FILLED);
      } else {
        checklistEntryWorkflowState.set(parentChecklistTemplateEntryId, WorkflowOverviewItemState.NOT_FILLED);
      }
    }

    ctx.patchState({ checklistEntryWorkflowState });
  }

  private setSignEnabled(
    ctx: StateContext<ChecklistFillOutXsStateModel>
  ): void {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!state.active || !state.checklistTemplateEntryIdsInOrder || !state.checklistTemplateEntries) {
      return;
    }

    const visibleSet: Set<string> = new Set();

    for (const checklistTemplateEntryId of state.checklistTemplateEntryIdsInOrder) {
      const checklistTemplateEntry: ChecklistTemplateEntry
        = state.checklistTemplateEntries.get(checklistTemplateEntryId);

      if (this.hasConditions(checklistTemplateEntry)) {
        // Check conditions (OR)
        this.checkConditions(checklistTemplateEntry, state, visibleSet, checklistTemplateEntryId);
      } else if (checklistTemplateEntry.type === ChecklistEntryType.TABLE_ROW) {
        if (visibleSet.has(checklistTemplateEntry.parentChecklistTemplateEntry.id)) {
          visibleSet.add(checklistTemplateEntryId);
        }
      } else {
        visibleSet.add(checklistTemplateEntryId);
      }
    }

    let signEnabled: boolean = true;

    for (const checklistEntry of state.checklistEntries.values()) {
      const checklistTemplateEntry: ChecklistTemplateEntry
        = state.checklistTemplateEntries.get(checklistEntry.checklistTemplateEntryId);

      if (visibleSet.has(checklistEntry.checklistTemplateEntryId)
        && checklistTemplateEntry?.required
        && !this.entryHasValue(checklistEntry.id, ctx)
      ) {
        signEnabled = false;
        break;
      }
    }

    ctx.patchState({
      signEnabled
    });
  }

  private entryHasValue(
    checklistEntryId: string,
    ctx: StateContext<ChecklistFillOutXsStateModel>,
    useSnapshotValues: boolean = false
  ): boolean {
    const state: ChecklistFillOutXsStateModel = ctx.getState();

    if (!checklistEntryId) {
      return null;
    }

    const checklistEntry: ChecklistEntry = state.checklistEntries.get(checklistEntryId);
    const checklistTemplateEntry: ChecklistTemplateEntry
      = state.checklistTemplateEntries.get(checklistEntry.checklistTemplateEntryId);

    if (!checklistEntry) {
      return null;
    }

    const entryValue: Partial<ChecklistEntry> = useSnapshotValues
      ? state.checklistEntryValuesSnapshotForFilter.get(checklistEntryId)
      : state.checklistEntryValues.get(checklistEntryId);
    const entryType: ChecklistEntryType = checklistEntry.type;

    if (entryType === ChecklistEntryType.TABLE_ROW) {
      return this.checkEntryValueForType(checklistTemplateEntry.tableType, entryValue, checklistTemplateEntry);
    } else {
      return this.checkEntryValueForType(entryType, entryValue, checklistTemplateEntry);
    }
  }

  private checkEntryValueForType(
    entryType: ChecklistEntryType,
    entryValue: Partial<ChecklistEntry>,
    checklistTemplateEntry: ChecklistTemplateEntry
  ): boolean {
    switch (entryType) {
      case ChecklistEntryType.OK_NOT_OK:
      case ChecklistEntryType.BOOLEAN:
        return !!entryValue?.optionId;
      case ChecklistEntryType.LIST:
        if (checklistTemplateEntry.renderOption === ChecklistEntryRenderOption.RADIO_BUTTONS) {
          return !!entryValue?.optionId;
        } else {
          return !!entryValue?.optionIds.length;
        }
      case ChecklistEntryType.COUNTER: {
        return !!entryValue?.integerValue;
      }
      case ChecklistEntryType.ASSESSMENT_LIST:
        return !!entryValue?.optionId;
      case ChecklistEntryType.DATE:
        return !!entryValue?.dateTimeValue;
      case ChecklistEntryType.NUMBER: {
        return entryValue?.integerValue !== null || entryValue?.doubleValue !== null;
      }
      case ChecklistEntryType.STRING: {
        return !!entryValue?.textValue;
      }
      default:
        return null;
    }
  }

  //endregion
}
